import React from "react";
import PropTypes from "prop-types";
import { Form as FormikForm, InputField, SelectField, CheckboxField } from "components/common_components/Formik";
import Grid from "@material-ui/core/Grid";
import * as Yup from "yup";
import * as Icons from "@material-ui/icons";
import { forms } from "../../Modules/Forms";
import { MenuItem } from "@material-ui/core";
import { getEventFields } from "entities/moduleUtils";
import { hasElements } from "utils/arrays";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import FormGroup from "@material-ui/core/FormGroup";

function Form({ data, mode, ...rest }) {
    const validationSchema = Yup.object({
        code: Yup.string().required("Required field").max(5),
        name: Yup.string().required("Required field").max(50),
        prefix: Yup.string().required("Required field").max(5),
        dbName: Yup.string().required("Required field").max(50),
        path: Yup.string()
            .required("Required field")
            .min(2)
            .max(100)
            .matches(/^\/[\w-]+(?=[/\r\n?]|$)/g, "The path must follow this pattern /my-path"),
        formName: Yup.string().required("Required field").max(50),
        iconName: Yup.string().max(50).nullable().notRequired(),
        calendarColor: Yup.string().max(50).nullable().notRequired(),
        eventFields: Yup.object().nullable().notRequired(),
    });

    const iconOptions = Object.keys(Icons)
        .filter((icon) => icon.endsWith("TwoTone"))
        .map((icon) => ({ value: icon }));

    function getIcon(iconName) {
        const Icon = Icons[iconName];
        return <Icon />;
    }

    function getModuleEventFields(form) {
        if (form.values.code) {
            return getEventFields(form.values.code);
        }
        return [];
    }

    function getInitialValues() {
        const { calendarFields, ...values } = data;

        if (calendarFields) {
            const fields = String(calendarFields).split(",");
            const eventFields = hasElements(fields) ? fields.reduce((map, field) => ({ ...map, [field]: true }), {}) : null;
            return { ...values, eventFields };
        }
        return values;
    }

    return (
        <FormikForm initialValues={getInitialValues()} validationSchema={validationSchema} {...rest}>
            {(form) => (
                <div className="layout">
                    <Grid container spacing={3} direction="row">
                        <Grid item xs={12} md={4}>
                            <InputField disabled={mode === "update"} fieldName="code" label="Code *" autoFocus uppercase />
                            <InputField fieldName="name" label="Name *" capitalize />
                            <InputField fieldName="description" label="Description" />
                            <InputField fieldName="prefix" label="Prefix *" uppercase helperText="e.g PF" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputField
                                fieldName="dbName"
                                label="Data Base Name *"
                                helperText="e.g dataBaseName"
                                disabled={mode === "update"}
                            />
                            <InputField fieldName="path" label="Path *" helperText="e.g /my-path" lowercase disabled={mode === "update"} />
                            <SelectField
                                fieldName="formName"
                                label="Form Name *"
                                options={forms.map((f) => ({ value: f, label: f }))}
                                disabled={mode === "update"}
                            />
                            <SelectField
                                fieldName="iconName"
                                label="Icon"
                                options={iconOptions}
                                renderOption={({ value }, index) => (
                                    <MenuItem
                                        key={index}
                                        value={value}
                                        style={{ padding: 5, flexDirection: "row", justifyContent: "space-between" }}
                                    >
                                        <div>{getIcon(value)}</div>
                                    </MenuItem>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Settings For Calendar</FormLabel>
                                <FormLabel component="legend">Appearance</FormLabel>
                                <FormGroup>
                                    <InputField
                                        type="color"
                                        style={{ padding: "0.1rem" }}
                                        fieldName="calendarColor"
                                        label="Calendar Color"
                                    />
                                </FormGroup>
                                <FormLabel component="legend">Event fields</FormLabel>
                                <FormGroup>
                                    {hasElements(getModuleEventFields(form)) &&
                                        getModuleEventFields(form).map(({ field, label }, index) => (
                                            <CheckboxField key={index} fieldName={`eventFields.${field}`} label={label} />
                                        ))}
                                </FormGroup>
                            </FormControl>
                        </Grid>
                    </Grid>
                </div>
            )}
        </FormikForm>
    );
}

Form.propTypes = {
    data: PropTypes.shape({
        code: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        prefix: PropTypes.string,
        dbName: PropTypes.string,
        path: PropTypes.string,
        formName: PropTypes.string,
        iconName: PropTypes.string,
        calendarColor: PropTypes.string,
        eventFields: PropTypes.string,
    }),
    mode: PropTypes.oneOf(["add", "update"]).isRequired,
};

Form.defaultProps = {
    data: {
        code: "",
        name: "",
        description: "",
        prefix: "",
        dbName: "",
        path: "/",
        formName: "",
        iconName: "AppsTwoTone",
        calendarColor: "",
        eventFields: "",
    },
};

export default Form;
